/**
 * GuestListSignupPage — /lista/:token
 *
 * Página pública donde un guest se apunta a la lista de un RRPP de un
 * organizador de pagafácil. Estética IES (dark editorial), pinta el
 * acento del organizador vía var(--brand).
 */
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import api from '../api/client';
import EntityThemeProvider from '../components/EntityThemeProvider';

interface Info {
  show_id: string;
  show_slug: string;
  show_title: string;
  show_date: string;
  show_time: string | null;
  venue_name: string | null;
  venue_city: string | null;
  rrpp_name: string;
  entity_name: string;
  entity_accent_color: string | null;
  slots_total: number;
  slots_taken: number;
  slots_left: number;
  is_full: boolean;
}

interface SignupResult {
  ok: boolean;
  invite_id: string;
  position: number;
  total_slots: number;
  slots_left: number;
}

function fmtDate(iso: string): string {
  if (!iso) return '';
  return new Date(iso + 'T00:00:00').toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
}

export default function GuestListSignupPage() {
  const { token = '' } = useParams();
  const [info, setInfo] = useState<Info | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState({ guest_name: '', guest_email: '', guest_phone: '', quantity: 1, notes: '' });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<SignupResult | null>(null);

  useEffect(() => {
    if (!token) return;
    document.title = 'Apúntate a la lista · Pagafácil';
    api.get<Info>(`/public/lista/${token}`)
      .then((r) => {
        setInfo(r.data);
        document.title = `Lista de ${r.data.rrpp_name} · ${r.data.show_title}`;
      })
      .catch((e) => setError(e?.response?.data?.detail || 'Enlace no válido'))
      .finally(() => setLoading(false));
  }, [token]);

  const validEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.guest_email);
  const canSubmit = !!info && !info.is_full && !!form.guest_name.trim() && validEmail && form.quantity >= 1;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit || busy) return;
    setBusy(true);
    setError(null);
    try {
      const r = await api.post<SignupResult>(`/public/lista/${token}/apuntarse`, form);
      setResult(r.data);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'No se pudo apuntar a la lista');
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return <Shell accent={null}><div style={{ padding: '120px 24px', textAlign: 'center', color: '#888' }}>Cargando…</div></Shell>;
  }

  if (error && !info) {
    return (
      <Shell accent={null}>
        <div style={{ padding: '120px 24px', textAlign: 'center' }}>
          <div style={{ fontFamily: 'Archivo, sans-serif', fontSize: 12, letterSpacing: 6, textTransform: 'uppercase', color: 'var(--brand)', marginBottom: 16, fontWeight: 700 }}>
            404
          </div>
          <h1 style={{ fontFamily: 'Archivo, sans-serif', fontSize: 32, textTransform: 'uppercase', letterSpacing: 3, margin: '0 0 12px', fontWeight: 900 }}>
            Enlace no válido
          </h1>
          <p style={{ color: '#888' }}>{error}</p>
          <Link to="/entradas" style={{ display: 'inline-block', marginTop: 24, color: 'var(--brand)', textDecoration: 'none', fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', fontSize: 12 }}>
            ← Cartelera
          </Link>
        </div>
      </Shell>
    );
  }

  return (
    <Shell accent={info!.entity_accent_color}>
      <section style={{ minHeight: '80vh', padding: '80px 24px 60px', maxWidth: 600, margin: '0 auto' }}>

        {result ? (
          <div style={{ background: '#111', border: '1px solid #222', padding: '40px 32px', textAlign: 'center' }}>
            <div style={{ fontSize: 48, marginBottom: 12, color: 'var(--brand)' }}>✓</div>
            <div style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 11, letterSpacing: 4, textTransform: 'uppercase', color: 'var(--brand)', marginBottom: 12, fontWeight: 700 }}>
              Estás en la lista
            </div>
            <h1 style={{ fontFamily: 'Archivo, sans-serif', fontSize: 26, letterSpacing: '0.02em', margin: '0 0 12px', fontWeight: 800, lineHeight: 1.15, color: '#fff' }}>
              {form.guest_name}, apuntadx a la lista de {info!.rrpp_name}
            </h1>
            <p style={{ color: '#aaa', lineHeight: 1.55, margin: '0 0 24px', fontFamily: 'DM Sans, sans-serif' }}>
              Te acabamos de enviar un email de confirmación a <b style={{ color: '#fff' }}>{form.guest_email}</b>.
              Cuando <b style={{ color: '#fff' }}>{info!.rrpp_name}</b> cierre la lista, recibirás un segundo email con el enlace para canjear tu entrada.
            </p>
            <div style={{ background: '#0a0a0a', padding: '16px 20px', borderLeft: `3px solid var(--brand)`, fontSize: 13, color: '#aaa', textAlign: 'left', fontFamily: 'DM Sans, sans-serif' }}>
              <div><b style={{ color: '#fff' }}>Evento:</b> {info!.show_title}</div>
              <div><b style={{ color: '#fff' }}>Fecha:</b> {fmtDate(info!.show_date)}</div>
              {info!.venue_name && <div><b style={{ color: '#fff' }}>Lugar:</b> {info!.venue_name}{info!.venue_city ? ' · ' + info!.venue_city : ''}</div>}
              <div style={{ marginTop: 6 }}><b style={{ color: '#fff' }}>Cantidad:</b> {form.quantity} entrada{form.quantity !== 1 ? 's' : ''}</div>
            </div>
          </div>
        ) : (
          <>
            <div style={{ marginBottom: 32, textAlign: 'center' }}>
              <div style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 11, letterSpacing: 4, textTransform: 'uppercase', color: 'var(--brand)', marginBottom: 10, fontWeight: 700 }}>
                Lista de {info!.rrpp_name} · {info!.entity_name}
              </div>
              <h1 style={{ fontFamily: 'Archivo, sans-serif', fontSize: 'clamp(28px, 5vw, 42px)', fontWeight: 900, lineHeight: 1.05, letterSpacing: -0.5, textTransform: 'uppercase', margin: '0 0 12px', color: '#fff' }}>
                {info!.show_title}
              </h1>
              <p style={{ color: '#aaa', margin: 0, fontSize: 14, fontFamily: 'DM Sans, sans-serif' }}>
                {fmtDate(info!.show_date)}
                {info!.show_time && <> · {info!.show_time}</>}
                {info!.venue_name && <> · {info!.venue_name}</>}
                {info!.venue_city && <>, {info!.venue_city}</>}
              </p>
              <div style={{ width: 60, height: 3, background: 'var(--brand)', margin: '20px auto 0' }} />
            </div>

            {info!.slots_total > 0 && (
              <div style={{ marginBottom: 24, padding: '12px 16px', background: '#111', border: '1px solid #222', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 13, color: '#aaa' }}>
                <span style={{ fontFamily: 'JetBrains Mono, monospace', letterSpacing: 1.5, textTransform: 'uppercase' }}>Plazas restantes</span>
                <span style={{ fontFamily: 'Archivo, sans-serif', fontWeight: 800, color: info!.is_full ? '#EF4444' : '#fff', fontSize: 16 }}>
                  {info!.is_full ? 'COMPLETA' : `${info!.slots_left} / ${info!.slots_total}`}
                </span>
              </div>
            )}

            {info!.is_full ? (
              <div style={{ background: '#111', border: '1px solid #222', padding: 40, textAlign: 'center' }}>
                <div style={{ fontFamily: 'Archivo, sans-serif', fontSize: 22, fontWeight: 900, letterSpacing: 2, textTransform: 'uppercase', color: '#EF4444', margin: '0 0 12px' }}>
                  Lista completa
                </div>
                <p style={{ color: '#888', margin: 0, fontFamily: 'DM Sans, sans-serif' }}>
                  Ya no quedan plazas en la lista de {info!.rrpp_name}. Puedes intentar comprar entrada normal desde la web del evento.
                </p>
              </div>
            ) : (
              <form onSubmit={submit} style={{ background: '#111', border: '1px solid #222', padding: 24 }}>
                <Field label="Nombre y apellidos" required>
                  <input value={form.guest_name} onChange={(e) => setForm({ ...form, guest_name: e.target.value })} required style={inputStyle} />
                </Field>
                <Field label="Email" required>
                  <input type="email" value={form.guest_email} onChange={(e) => setForm({ ...form, guest_email: e.target.value })} required style={inputStyle} />
                </Field>
                <Field label="Teléfono">
                  <input type="tel" value={form.guest_phone} onChange={(e) => setForm({ ...form, guest_phone: e.target.value })} style={inputStyle} />
                </Field>
                <Field label="Cantidad">
                  <input
                    type="number" min={1} max={info!.slots_left || 20}
                    value={form.quantity}
                    onChange={(e) => setForm({ ...form, quantity: Math.max(1, Number(e.target.value) || 1) })}
                    style={inputStyle}
                  />
                </Field>
                <Field label="Notas (opcional)">
                  <textarea
                    value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    rows={2}
                    style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }}
                    placeholder="Ej: alergias, horario de llegada, etc."
                  />
                </Field>

                {error && (
                  <div style={{ padding: '10px 14px', background: 'rgba(239,68,68,.15)', border: `1px solid #EF4444`, color: '#f5c8c8', marginTop: 8, fontSize: 13 }}>
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={!canSubmit || busy}
                  style={{
                    marginTop: 20, width: '100%', padding: '16px 18px',
                    background: canSubmit && !busy ? 'var(--brand)' : '#333',
                    color: canSubmit && !busy ? 'var(--brand-ink)' : '#888',
                    border: 'none',
                    fontFamily: 'Archivo, sans-serif', fontWeight: 700, fontSize: 14,
                    letterSpacing: 3, textTransform: 'uppercase',
                    cursor: canSubmit && !busy ? 'pointer' : 'not-allowed',
                  }}
                >
                  {busy ? 'Apuntando…' : 'Apuntarme a la lista'}
                </button>
                <div style={{ marginTop: 12, fontSize: 11, color: '#666', textAlign: 'center', lineHeight: 1.5, fontFamily: 'JetBrains Mono, monospace', letterSpacing: 0.5 }}>
                  Al apuntarte aceptas que {info!.rrpp_name} pueda contactarte por este email.
                  Quedas en lista pendiente hasta que el RRPP la cierre.
                </div>
              </form>
            )}
          </>
        )}

        <div style={{ marginTop: 32, textAlign: 'center' }}>
          <Link to={`/o/${info?.entity_name ? '' : ''}`} style={{ color: '#666', fontSize: 12, textDecoration: 'none', fontFamily: 'JetBrains Mono, monospace', letterSpacing: 2, textTransform: 'uppercase' }}>
            Pagafácil · sin app, sin login
          </Link>
        </div>
      </section>
    </Shell>
  );
}

function Shell({ children, accent }: { children: React.ReactNode; accent: string | null }) {
  return (
    <EntityThemeProvider accentColor={accent}>
      <div style={{
        fontFamily: 'DM Sans, system-ui, sans-serif',
        background: '#0a0a0a', color: '#f5f0e8', minHeight: '100vh',
      }}>
        {children}
      </div>
    </EntityThemeProvider>
  );
}

function Field({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 14 }}>
      <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', color: '#888', fontWeight: 700 }}>
        {label}{required && <span style={{ color: 'var(--brand)' }}> *</span>}
      </span>
      {children}
    </label>
  );
}

const inputStyle: React.CSSProperties = {
  padding: '12px 14px',
  border: '1px solid #333',
  background: '#0a0a0a',
  fontSize: 15,
  color: '#fff',
  fontFamily: 'DM Sans, sans-serif',
  outline: 'none',
};
